// Buy flow: turn the current configuration into a real Shopify checkout via the
// shopify-checkout Edge Function. The function recomputes the price server-side
// and creates the Shopify draft order, so the viewer never sees the store token
// and a shopper can never tamper the price.

import type { ConfigState } from "@/types";

const BRIDGE = "https://vmvbvbrcdphknjibekgg.supabase.co/functions/v1/shopify-checkout";

// The configurator slug, which the bridge needs to load the trusted config.
// Resolution order:
//   1. ?nvslug=<slug> explicit param (an embed may pass it directly)
//   2. the slug= inside the ?config=<get-configurator?slug=...> URL
// Returns null when there is no slug (e.g. the admin data: URL preview), so the
// Buy button can stay a harmless demo there instead of erroring.
export function currentSlug(): string | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const explicit = params.get("nvslug");
    if (explicit) return explicit;
    const configURL = params.get("config");
    if (configURL) {
      const m = /[?&]slug=([^&]+)/.exec(configURL);
      if (m) return decodeURIComponent(m[1]);
    }
  } catch {
    /* ignore */
  }
  return null;
}

export interface CheckoutResult {
  checkoutUrl: string;
  price: number;
  currency: string;
  title: string;
}

// POST the selected options to the bridge and return the Shopify checkout URL.
// Throws with a readable message on any failure so the caller can show a toast.
export async function createCheckout(
  slug: string,
  state: ConfigState,
  quantity = 1,
): Promise<CheckoutResult> {
  const res = await fetch(BRIDGE, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ slug, state, quantity }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.checkoutUrl) {
    throw new Error(data?.error || `Checkout failed (${res.status})`);
  }
  return data as CheckoutResult;
}

// Send the shopper to Shopify checkout. The viewer is usually in an iframe on the
// client product page, so navigate the TOP window, falling back to this window.
export function goToCheckout(url: string): void {
  try {
    if (window.top && window.top !== window.self) {
      window.top.location.href = url;
      return;
    }
  } catch {
    /* cross-origin top access blocked, fall through to self */
  }
  window.location.href = url;
}
